import { strToU8, strFromU8, unzipSync, zipSync } from 'fflate';
import type { CollageDocument } from './document';
import { validateDocument } from './schemas';
import { serializeDocument } from './serialization';

export type ProjectBundle = {
  document: CollageDocument;
  assets: Record<string, Uint8Array>;
  fonts?: Record<string, Uint8Array>;
};

const MAX_ARCHIVE_BYTES = 512 * 1024 * 1024;
const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;
const MAX_ASSET_BYTES = 50 * 1024 * 1024;
const MAX_FONT_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 1024 * 1024 * 1024;
const MAX_ENTRIES = 500;

export function encodeProjectArchive(bundle: ProjectBundle): Uint8Array {
  const document = validateDocument(bundle.document);
  const entries: Record<string, Uint8Array> = { 'document.json': strToU8(serializeDocument(document)) };
  for (const asset of Object.values(document.assets)) {
    const bytes = bundle.assets[asset.id];
    if (!bytes) throw new Error(`Manjka vsebina fotografije ${asset.originalName}.`);
    if (bytes.byteLength > MAX_ASSET_BYTES) throw new Error(`Fotografija ${asset.originalName} presega omejitev 50 MiB.`);
    entries[asset.path] = bytes;
  }
  if (Object.keys(bundle.assets).some((id) => !document.assets[id])) throw new Error('Projekt vsebuje nepričakovano fotografijo.');
  for (const font of Object.values(document.fonts)) {
    const bytes = bundle.fonts?.[font.id];
    if (!bytes) throw new Error(`Manjka vsebina pisave ${font.family}.`);
    if (bytes.byteLength > MAX_FONT_BYTES) throw new Error(`Pisava ${font.family} presega omejitev 10 MiB.`);
    entries[font.path] = bytes;
  }
  if (Object.keys(bundle.fonts ?? {}).some((id) => !document.fonts[id])) throw new Error('Projekt vsebuje nepričakovano pisavo.');
  const archive = zipSync(entries, { level: 1 });
  if (archive.byteLength > MAX_ARCHIVE_BYTES) throw new Error('Projekt presega omejitev arhiva 512 MiB.');
  return archive;
}

export function decodeProjectArchive(archive: Uint8Array): ProjectBundle {
  if (archive.byteLength === 0 || archive.byteLength > MAX_ARCHIVE_BYTES) throw new Error('Projektna datoteka je prazna ali presega omejitev 512 MiB.');
  let entries: Record<string, Uint8Array>;
  let entryCount = 0;
  let declaredTotalBytes = 0;
  try {
    entries = unzipSync(archive, {
      filter: (entry) => {
        entryCount += 1;
        declaredTotalBytes += entry.originalSize;
        if (entryCount > MAX_ENTRIES || declaredTotalBytes > MAX_TOTAL_BYTES) throw new Error('Project archive exceeds extraction limits');
        if (entry.name === 'document.json') {
          if (entry.originalSize > MAX_DOCUMENT_BYTES) throw new Error('Project metadata exceeds limits');
          return true;
        }
        if (!/^assets\/[A-Za-z0-9_-]{1,128}\.(?:jpe?g|png|webp|svg)$/i.test(entry.name) && !/^fonts\/[A-Za-z0-9_-]{1,128}\.(?:ttf|otf)$/i.test(entry.name)) {
          throw new Error('Project archive contains an invalid file');
        }
        if (entry.name.startsWith('assets/') && entry.originalSize > MAX_ASSET_BYTES) throw new Error('Project asset exceeds limits');
        if (entry.name.startsWith('fonts/') && entry.originalSize > MAX_FONT_BYTES) throw new Error('Project font exceeds limits');
        return true;
      },
    });
  } catch {
    throw new Error('Projektna datoteka ni veljaven ZIP arhiv.');
  }
  const paths = Object.keys(entries);
  if (paths.length > MAX_ENTRIES || !entries['document.json']) throw new Error('Projektna datoteka nima pričakovane strukture.');
  let totalBytes = 0;
  for (const [entryPath, bytes] of Object.entries(entries)) {
    totalBytes += bytes.byteLength;
    if (totalBytes > MAX_TOTAL_BYTES) throw new Error('Projekt je po razširitvi prevelik.');
    if (entryPath === 'document.json') {
      if (bytes.byteLength > MAX_DOCUMENT_BYTES) throw new Error('Opis projekta presega omejitev 5 MiB.');
    } else if (/^assets\//.test(entryPath) ? (!/^assets\/[A-Za-z0-9_-]{1,128}\.(?:jpe?g|png|webp|svg)$/i.test(entryPath) || bytes.byteLength > MAX_ASSET_BYTES) : (!/^fonts\/[A-Za-z0-9_-]{1,128}\.(?:ttf|otf)$/i.test(entryPath) || bytes.byteLength > MAX_FONT_BYTES)) {
      throw new Error('Projekt vsebuje nepričakovano ali preveliko datoteko.');
    }
  }

  let document: CollageDocument;
  try {
    document = validateDocument(JSON.parse(strFromU8(entries['document.json'])) as unknown);
  } catch {
    throw new Error('Opis projekta ni veljaven ali je nezdružljiv s to različico aplikacije.');
  }
  const assets: Record<string, Uint8Array> = {};
  const fonts: Record<string, Uint8Array> = {};
  const expectedPaths = new Set(['document.json']);
  for (const asset of Object.values(document.assets)) {
    const bytes = entries[asset.path];
    if (!bytes) throw new Error(`V projektu manjka fotografija ${asset.originalName}.`);
    expectedPaths.add(asset.path);
    assets[asset.id] = bytes;
  }
  for (const font of Object.values(document.fonts)) {
    const bytes = entries[font.path];
    if (!bytes) throw new Error(`V projektu manjka pisava ${font.family}.`);
    expectedPaths.add(font.path);
    fonts[font.id] = bytes;
  }
  if (paths.some((entryPath) => !expectedPaths.has(entryPath))) throw new Error('Projekt vsebuje fotografije, ki niso navedene v opisu.');
  return { document, assets, fonts };
}
